/**
 * The bank home view of the Clarid Agent (in-bank): where this bank stands, and the playbooks a compliance
 * officer can start. Every action here starts in the chat, so it is part of the conversation and the
 * audit record; nothing runs from this view directly.
 */
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  PlayIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { agentApi } from '../../lib/agent';
import ConnectedStorage from './ConnectedStorage';

const SIGNOFF: Record<string, string> = {
  DRAFT: 'Draft', PREPARED: 'Awaiting review', REVIEWED: 'Awaiting approval', RETURNED: 'Returned for changes',
};

export default function AgentHome({
  teamId,
  onAsk,
  onOpenConversation,
}: {
  teamId: string;
  onAsk: (text: string) => void;
  onOpenConversation: (id: string) => void;
}) {
  const { data: home } = useQuery({
    queryKey: ['agent-home', teamId],
    queryFn: () => agentApi.home(teamId).then((r) => r.data.home),
    staleTime: 30_000,
  });
  if (!home) return null;
  const r = home.readiness;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 text-left">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={r && r.blocked.length === 0 ? CheckCircleIcon : ExclamationTriangleIcon}
          tone={!r ? 'muted' : r.blocked.length === 0 ? 'ok' : 'warn'}
          title={r ? `${r.ready} of ${r.total} capabilities ready` : 'Readiness unavailable'}
          detail={r ? (r.blocked.length ? r.blocked.map((b) => b.capability).join(', ') + ' blocked' : r.warnings ? `${r.warnings} warning(s) to review` : 'No open data issues') : 'The readiness check could not run just now.'}
          onClick={() => onAsk('Is our data ready for this year’s fair lending review?')}
        />
        <Tile
          icon={WrenchScrewdriverIcon}
          tone={home.pendingFixes ? 'warn' : 'ok'}
          title={home.pendingFixes ? `${home.pendingFixes} data fix(es) waiting` : 'No data fixes waiting'}
          detail={home.pendingFixes ? 'Each needs an Owner or Compliance Officer to approve.' : 'Ask Clarid to look for correctable problems.'}
          onClick={() => onAsk(home.pendingFixes ? 'Are any data fixes waiting for approval?' : 'Find any data problems Clarid can fix.')}
        />
        <Tile
          icon={DocumentCheckIcon}
          tone={home.awaitingSignoff.length ? 'warn' : 'ok'}
          title={home.awaitingSignoff.length ? `${home.awaitingSignoff.length} deliverable(s) awaiting sign-off` : 'Nothing awaiting sign-off'}
          detail={home.awaitingSignoff.slice(0, 2).map((d) => `${d.fileName}: ${SIGNOFF[d.status] ?? d.status}`).join(' · ') || 'Signed deliverables are final.'}
          onClick={() => onAsk('What’s waiting for my sign-off?')}
        />
        <Tile
          icon={ClipboardDocumentCheckIcon}
          tone={!home.recommendations ? 'muted' : home.recommendations.overdue || home.recommendations.repeats ? 'warn' : 'ok'}
          title={!home.recommendations ? 'Recommendations unavailable' : home.recommendations.open ? `${home.recommendations.open} open recommendation(s)` : 'No open recommendations'}
          detail={!home.recommendations ? 'They could not be read just now.' : [
            home.recommendations.overdue ? `${home.recommendations.overdue} overdue` : '',
            home.recommendations.repeats ? `${home.recommendations.repeats} repeat finding(s)` : '',
          ].filter(Boolean).join(' · ') || 'Record the latest review’s Focal Points to track them.'}
          onClick={() => onAsk(home.recommendations?.open ? 'Show the open recommendations.' : 'Record the latest analysis’s Focal Points as recommendations to track.')}
        />
      </div>

      {home.openPlans.length > 0 && (
        <div className="rounded-lg border border-gray-200 px-4 py-3">
          <p className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
            <ClipboardDocumentListIcon className="h-4 w-4" /> Open plans
          </p>
          <ul className="space-y-1">
            {home.openPlans.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  disabled={!p.conversationId}
                  onClick={() => p.conversationId && onOpenConversation(p.conversationId)}
                  className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-default"
                >
                  <span>{p.title}</span>
                  <span className="text-xs text-gray-500">{p.status === 'PROPOSED' ? 'Awaiting approval' : 'Running'}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConnectedStorage teamId={teamId} />

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Playbooks</p>
        <div className="grid gap-2 sm:grid-cols-3">
          {home.playbooks.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onAsk(`Run the ${p.name.toLowerCase()} playbook.`)}
              className="rounded-lg border border-gray-200 px-3 py-2.5 text-left hover:border-ocean-300 hover:bg-ocean-50"
            >
              <span className="flex items-center gap-1.5 text-sm font-medium text-navy-900"><PlayIcon className="h-4 w-4 text-ocean-600" /> {p.name}</span>
              <span className="mt-0.5 block text-xs text-gray-500">{p.description}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tile({
  icon: Icon, tone, title, detail, onClick,
}: {
  icon: typeof CheckCircleIcon; tone: 'ok' | 'warn' | 'muted'; title: string; detail: string; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="rounded-lg border border-gray-200 px-3 py-2.5 text-left hover:border-ocean-300 hover:bg-ocean-50">
      <span className={clsx('flex items-center gap-1.5 text-sm font-medium', tone === 'ok' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-gray-500')}>
        <Icon className="h-4 w-4 shrink-0" /> {title}
      </span>
      <span className="mt-0.5 block text-xs text-gray-500 line-clamp-2">{detail}</span>
    </button>
  );
}
